import { Alert } from '@zopauk/web-ui';
import { PostCard } from '@/components/PostCard';
import { Pagination } from '@/components/Pagination';
import { Placeholder } from '@/components/Placeholder';
import type { AllPostsContext } from '@/utils/models/allPosts/allPosts';
import { type PostsForAllPostsPageFragment, useFeaturedPostsQuery, useAllPostsQuery } from '@/utils/dato/generated';

const PostsForAllPostsPage = ({ pageContext }: { pageContext: AllPostsContext } & PostsForAllPostsPageFragment) => {
  const { error, data } = useAllPostsQuery({
    perPage: pageContext.pagination.perPage,
    skip: pageContext.pagination.skip,
  });

  const { data: featured } = useFeaturedPostsQuery();

  if (data) {
    return (
      <div className="container mx-auto px-4">
        <div className="bg-base-100 rounded-lg px-4 pt-8 my-10 pb-10 text-center">
          <h1 className="text-7xl font-serif font-semibold mb-3">Zopa blog</h1>
          <p className="text-lg">
            Page {pageContext.pagination.currentPage} of {pageContext.pagination.totalPages}
          </p>
        </div>
        {featured ? (
          <div className="flex flex-wrap">
            {featured.posts.map((post, id) => (
              <div className="w-full md:w-1/2 px-6 mb-6 lg:mb-10 flex align-sretch" key={'featured-' + id}>
                <PostCard data={post} />
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap">
          {data.posts.map((post) => (
            <div className="w-full md:w-1/2 lg:w-1/3 px-6 mb-6 flex" key={post.slug}>
              <PostCard data={post} />
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-8">
          <Pagination basePath="/blog" {...pageContext.pagination} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative mt-10 mb-8 lg:mb-11 flex flex-col max-w-screen-xl mx-auto w-full px-4">
        <Alert variant="warning">We've been unable to load our posts</Alert>
      </div>
    );
  }

  return (
    <div className="mt-10 mb-8 lg:mb-11 mt-10 mb-8 lg:mb-11 max-w-screen-xl mx-auto px-4">
      <div className="bg-base-100 rounded-lg px-4 pt-8 pb-10 mb-10 text-center">
        <div className="flex flex-col items-center">
          <Placeholder className="mx-auto w-[380px] h-[46px]" />
          <Placeholder className="mx-auto mb-0 w-[200px] h-[26px]" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 9 }).map((_, i) => (
          <div className="space-y-3" key={i}>
            <Placeholder className="w-full h-[160px] rounded-lg" />
            <Placeholder className="w-3/4 h-[20px]" />
            <Placeholder className="w-1/2 h-[16px]" />
          </div>
        ))}
      </div>
      <div className="px-5 mx-auto flex max-w-[825px] space-y-6">
        <Placeholder className="mx-auto w-[180px] h-[52px]" />
      </div>
    </div>
  );
};

export default PostsForAllPostsPage;
